import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import { Loader2, Gift } from 'lucide-react';

interface SubscriptionLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'free-trial-ended' | 'plan-limit-reached';
}

const SubscriptionLimitModal: React.FC<SubscriptionLimitModalProps> = ({
  isOpen,
  onClose,
  type
}) => {
  const navigate = useNavigate();
  const [monthlyLimit, setMonthlyLimit] = useState<number | null>(null);
  const [nextRenewalDate, setNextRenewalDate] = useState<Date | null>(null);
  const [roundedTotalImages, setRoundedTotalImages] = useState<number>(15);
  const [showExtendTrial, setShowExtendTrial] = useState(false);
  const [shortName, setShortName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const opinionWhatsAppUrl = `https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba para dar mi opinión sobre el programa.`)}`;
  const [showOpinionQR, setShowOpinionQR] = useState(false);
  const opinionQRImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(opinionWhatsAppUrl)}`;
  const [canExtendTrial, setCanExtendTrial] = useState(false);
  const [promoMs, setPromoMs] = useState<number>(0);

  
  const [openingPortal, setOpeningPortal] = useState(false);

  // Country/provider settings
  const [pais, setPais] = useState<string>('Argentina');
  const isArgentina = (pais || '').toLowerCase() === 'argentina';

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningPortal(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      const resp = await fetch('https://ai-agency-dt7d.onrender.com/webhook/billing-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id })
      });
      if (!resp.ok) throw new Error('No se pudo abrir el portal de facturación');
      const data = await resp.json();
      if (data?.url) {
        window.location.href = data.url as string;
      } else {
        throw new Error('URL inválida recibida');
      }
    } catch (e: any) {
      alert(e?.message || 'Error al abrir el portal de facturación');
      setOpeningPortal(false);
    }
  };

  const handleGoToPlan = () => {
    navigate('/subscription');
    onClose();
  };

  // Helpers to compute current billing cycle end based on paid_plan_start_date
  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const getBillingCycleRange = (paidPlanStartDate: string, upgradePlanDate?: string | null) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const referenceDate = upgradePlanDate ? new Date(upgradePlanDate) : subscriptionDate;
    const anchorDay = referenceDate.getDate();

    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };

    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(subscriptionDate.getFullYear(), subscriptionDate.getMonth(), subscriptionDate.getDate(), 0, 0, 0, 0);
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString(), nextRenewal: end };
  };

  // When opening on plan-limit, fetch monthly limit and compute next renewal date
  useEffect(() => {
    if (!isOpen || type !== 'plan-limit-reached') return;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) return;

        const { data: company } = await supabase
          .from('company_profiles')
          .select('max_fotos_mes, paid_plan_start_date, upgrade_plan_date')
          .eq('id_company', profile.id_company)
          .single();
        if (!company) return;
        if (typeof company.max_fotos_mes === 'number') {
          setMonthlyLimit(company.max_fotos_mes);
        }
        let renewal: Date | null = null;
        if (company.paid_plan_start_date) {
          const { nextRenewal } = getBillingCycleRange(company.paid_plan_start_date, company.upgrade_plan_date);
          renewal = nextRenewal;
        } else {
          const { end } = getCurrentMonthRange();
          renewal = new Date(end);
        }
        setNextRenewalDate(renewal);
      } catch (e) {
        // Non-blocking: leave defaults
        console.warn('No se pudo obtener datos de límite/renovación:', e);
      }
    })();
  }, [isOpen, type]);

  // Promo countdown: always start at 57:00 when the modal opens in free-trial-ended
  useEffect(() => {
    if (!isOpen || type !== 'free-trial-ended') return;
    setPromoMs(57 * 60 * 1000);
    const id = setInterval(() => {
      setPromoMs(prev => {
        if (prev <= 1000) {
          clearInterval(id);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen, type]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company, name_short')
          .eq('id', user.id)
          .single();
        if ((profile as any)?.name_short) setShortName((profile as any).name_short as string);
        if (!profile?.id_company) return;
        const { data: company } = await supabase
          .from('company_profiles')
          .select('nombre, pais')
          .eq('id_company', profile.id_company)
          .single();
        if ((company as any)?.nombre) setCompanyName((company as any).nombre as string);
        if ((company as any)?.pais) setPais((company as any).pais as string);
      } catch {}
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || type !== 'free-trial-ended') return;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) return;

        const { data: company } = await supabase
          .from('company_profiles')
          .select('imagenes_totales_generadas')
          .eq('id_company', profile.id_company)
          .single();
        const total = (company as any)?.imagenes_totales_generadas as number | undefined;
        if (typeof total === 'number' && total >= 0) {
          const rounded = Math.floor(total / 5) * 5;
          setRoundedTotalImages(Math.max(5, rounded));
        }
        setCanExtendTrial(typeof total === 'number' ? total < 22 : false);
      } catch {}
    })();
  }, [isOpen, type]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
      <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
        <div className="p-5">
          {type === 'free-trial-ended' ? (
            <>
              {!showExtendTrial || !canExtendTrial ? (
                <>
                  {/* Big centered title */}
                  <h2 className="text-[17px] sm:text-[17px] font-bold text-brand-black/90 text-center mb-4">
                    ¡QUÉDATE CON NOSOTROS!
                  </h2>
                  <hr className="border-neutral-200 mb-5" />

                  {/* Gift offer card */}
                  <div className="mb-6">
                    <div className="rounded-md border border-brand-primary/20 bg-brand-primary/5 p-5 flex items-center justify-center gap-4">
                      <Gift className="w-14 h-14 sm:w-16 sm:h-16 text-brand-primary shrink-0" />
                      <div>
                        {isArgentina ? (
                          <>
                            <p className="text-[17px] sm:text-[17px] text-neutral-600 leading-tight">Te regalamos</p>
                            <p className="text-[17px] sm:text-[17px] font-bold text-brand-primary leading-tight">30 fotos gratis <span className="font-normal text-neutral-600">más</span></p>
                          </>
                        ) : (
                          <>
                            <p className="text-[17px] sm:text-[17px] text-neutral-600 leading-tight">Te damos un</p>
                            <p className="text-[17px] sm:text-[17px] font-bold text-brand-primary leading-tight">50% de descuento</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description text */}
                  <p className="text-[15px] text-neutral-600 mb-6 text-center sm:text-left">
                    {isArgentina ? (
                      <>Queremos que continues mejorando tu contenido con <strong className="text-brand-black/90">Estudio Atlas</strong>. Por eso te <strong className="text-brand-black/90">ofrecemos 30 fotos gratis</strong>, suscribiendote a cualquiera de los planes <strong className="text-brand-black/90">en tu primer mes</strong>.</>
                    ) : (
                      <>Queremos que continues mejorando tu contenido con <strong className="text-brand-black/90">Estudio Atlas</strong>. Por eso te <strong className="text-brand-black/90">ofrecemos un 50% de descuento</strong> en tu <strong className="text-brand-black/90">primer mes</strong> de suscripción a cualquiera de los planes.</>
                    )}
                  </p>

                  {/* CTA button */}
                  <button
                    onClick={handleGoToPlan}
                    className="w-full inline-flex items-center justify-center h-10 px-5 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    Reclamar mi descuento ahora
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">Extendé tu prueba gratis</h3>
                  <p className="text-[15px] text-brand-black/90 mb-6">Danos tu opinión sobre que te está pareciendo la plataforma, y recibe a cambio más imágenes gratis para seguir generando. 😊</p>
                  <div className="flex justify-end gap-2.5">
                    <button
                      onClick={() => {
                        try {
                          if (window.matchMedia && window.matchMedia('(min-width: 640px)').matches) {
                            setShowOpinionQR(true);
                          } else {
                            window.open(opinionWhatsAppUrl, '_blank');
                          }
                        } catch {
                          window.open(opinionWhatsAppUrl, '_blank');
                        }
                      }}
                      className="inline-flex items-center h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                    >
                      Dar opinión
                    </button>
                    <button
                      onClick={() => { setShowExtendTrial(false); setShowOpinionQR(false); }}
                      className="inline-flex items-center h-10 px-4 border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                    >
                      Volver
                    </button>
                  </div>
                  {showOpinionQR && (
                    <div className="mt-6 hidden sm:flex flex-col items-center">
                      <img src={opinionQRImageUrl} alt="QR para abrir WhatsApp y dar tu opinión" className="w-48 h-48 rounded-md" />
                      <p className="text-[13px] text-neutral-500 mt-3 text-center">
                        Escaneá el código QR con la cámara de tu celular, para dar tu opinión y recibir las imágenes gratis.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                Has alcanzado el límite de imágenes de tu plan
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-2">
                {monthlyLimit ? (
                  <>Alcanzaste el límite de {monthlyLimit} fotos de este mes.</>
                ) : (
                  <>Alcanzaste el límite de imágenes de tu plan por este mes.</>
                )}
              </p>
              {nextRenewalDate && (
                <p className="text-[15px] text-neutral-500 mb-4">
                  Tu cupo de imágenes se renovará el {nextRenewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              )}
              <p className="text-[15px] text-brand-black/90 mb-6">
                Para seguir generando imágenes ya mismo, puedes mejorar tu plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                {isArgentina ? (
                  <button
                    onClick={handleGoToPlan}
                    className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    Mejorar mi plan
                  </button>
                ) : (
                  <button
                    onClick={handleOpenBillingPortal}
                    disabled={openingPortal}
                    className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    {openingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gestionar subscripción'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionLimitModal;
